/**
 * What the ground is made of under a character (ADR-085's amendment for P1.9-T84): the surfaces a footstep sounds on
 * and the words that tell a terrain, a ground layer or a tileset by its name, as `propIsGround` tells ground art.
 * The list is the one place a surface is named: a new surface is a new folder under `sfx/player/walk/` and one entry.
 */

/**
 * One surface: its id, which is its folder's name, and the lower-case words that name it.
 */
export interface SurfaceSpec {
  readonly id: string;
  readonly words: readonly string[];
}

/**
 * The surfaces, in the order a name holding the words of two is read: liquid first, since flooded grass splashes, then
 * snow, sand, stone, wood, dirt and grass. Gravel reads as dirt until it has a folder of its own.
 */
export const SURFACES: readonly SurfaceSpec[] = Object.freeze([
  { id: "water", words: ["water", "flooded", "river", "lake", "swamp", "pond", "blood"] },
  { id: "snow", words: ["snow", "frost", "frozen"] },
  { id: "sand", words: ["sand", "beach"] },
  { id: "stone", words: ["stone", "cobble", "brick", "rock", "paved"] },
  { id: "wood", words: ["wood", "plank", "bridge", "deck", "floor"] },
  { id: "dirt", words: ["dirt", "gravel", "soil", "mud", "path"] },
  { id: "grass", words: ["grass", "leaves", "meadow", "moss"] },
]);

/**
 * The surface of ground nothing names, and of a surface with no files.
 */
export const DEFAULT_SURFACE = "dirt";

/**
 * Words naming what is drawn on a ground layer and is not ground: walls, fences, cliffs, holes, borders, bushes,
 * roofs, falls and prop sheets. A name holding one has no surface, so what lies under it sounds instead.
 */
const NOT_GROUND_WORDS: readonly string[] = ["wall", "fence", "cliff", "hole", "border", "bush", "roof", "waterfall", "props", "sprites", "house", "decoration"];

/**
 * The separators after which a name says what the ground fades into or lies on (`dirt to darker grass`,
 * `Dirt 1 on grass`), which is not what it is.
 */
const BEYOND = [" to ", " on "];

/**
 * Whether a name is a listed surface.
 *
 * @param id The candidate.
 * @returns True for the id of a surface in `SURFACES`.
 */
export function isSurface(id: string): boolean {
  return SURFACES.some((surface) => surface.id === id);
}

/**
 * The first listed surface one of whose words a text holds.
 *
 * @param text Lower-case text.
 * @returns The surface's id, or null when it holds no word.
 */
function surfaceIn(text: string): string | null {
  return SURFACES.find((surface) => surface.words.some((word) => text.includes(word)))?.id ?? null;
}

/**
 * The surface a terrain, a ground layer or a tileset is, by its name: nothing for a name with a word of
 * `NOT_GROUND_WORDS`; otherwise the first listed surface with a word in the part that says what the ground is, before
 * ` to ` or ` on ` and after a tileset id's pack and colon; otherwise the first with a word anywhere in the name.
 *
 * @param name A terrain's name, a layer's name or a tileset's id.
 * @returns The surface's id, or null when the name says none.
 * @example
 * groundSurface("water to grass (river orientation)");
 * // "water"
 */
export function groundSurface(name: string): string | null {
  const lower = name.toLowerCase();
  const text = lower.slice(lower.indexOf(":") + 1);
  if (NOT_GROUND_WORDS.some((word) => text.includes(word))) {
    return null;
  }
  let head = text;
  for (const separator of BEYOND) {
    const at = head.indexOf(separator);
    if (at > 0) {
      head = head.slice(0, at);
    }
  }
  return surfaceIn(head) ?? surfaceIn(text);
}
